import { z } from 'zod';

const Id = z.string().min(1).max(64);

// Joint appointment for several projects of the same developer (setter spec §08.4, §08.5, UAT-34). One Calendly booking
// (the primary project's) linked to the contact's other opportunities, only when one advisor can present every project
// (Calendly event type shared by the projects + the assigned advisors' project access); otherwise separate appointments.

export const JOINT_INELIGIBLE_CODES = [
  'single_project',
  'too_many_projects',
  'no_opportunity',
  'opportunity_inactive',
  'human_owned',
  'already_booked',
  'calendar_unavailable',
  'different_calendars',
  'no_shared_advisor',
  'advisor_not_authorized',
] as const;
export const JointIneligibleCodeSchema = z.enum(JOINT_INELIGIBLE_CODES);
export type JointIneligibleCode = z.infer<typeof JointIneligibleCodeSchema>;

export const JointAppointmentQuerySchema = z.object({ projectId: Id });

export const JointAppointmentOptionsSchema = z.object({
  primaryProjectId: Id,
  /** The joint visit that can be booked now (null → separate appointments). */
  offer: z
    .object({
      projectIds: z.array(Id),
      /** The primary project's appointment type for the event type shared by every project. */
      appointmentTypeId: Id,
      /** Assigned advisor authorized for every project; null = Calendly's round robin among the shared event type's hosts. */
      advisorMembershipId: Id.nullable(),
    })
    .nullable(),
  otherProjects: z.array(
    z.object({
      projectId: Id,
      projectName: z.string(),
      leadInterestId: Id,
      eligible: z.boolean(),
      code: JointIneligibleCodeSchema.nullable(),
      reason: z.string().nullable(),
    }),
  ),
});
export type JointAppointmentOptions = z.infer<typeof JointAppointmentOptionsSchema>;

/** A linked (non-primary) opportunity of a joint appointment. */
export const JointProjectLinkSchema = z.object({ projectId: Id, projectName: z.string(), leadInterestId: Id });
export type JointProjectLink = z.infer<typeof JointProjectLinkSchema>;
